// The booking window, defined once and applied by both the flow and the function.
//
// Two edges:
//
//   lower  now. She can start a date night immediately, so the edge is the
//          current minute rather than a lead time.
//
//          The flow and the function still disagree deliberately, and in the
//          same direction as before: the function is the more permissive of
//          the two, so a pick the flow allowed cannot be refused purely
//          because she spent twenty seconds reading the summary. That
//          asymmetry is what makes the guarantee hold; only the numbers moved.
//
//          Discord will not accept a scheduled event at or before the present
//          instant — it answers GUILD_SCHEDULED_EVENT_SCHEDULE_PAST, verified
//          against the live API — so an immediate booking is created a minute
//          ahead. See startNoEarlierThan.
//
//   upper  a CALENDAR-DAY bound in her zone, running to the last moment of the
//          180th day after today. Not an elapsed-hours count: those disagree by
//          up to a day, so the flow would offer a day the function refuses.

import { Temporal } from 'temporal-polyfill';

/**
 * The flow accepts the current minute: at 21:00:40 a pick of 21:00 is 40
 * seconds "late" and must still count, because that is what choosing now
 * means when the picker works in whole minutes.
 */
export const CLIENT_MIN_LEAD_MINUTES = -1;

/**
 * The function tolerates a pick up to five minutes old, so time spent on the
 * summary screen cannot turn an allowed pick into a refused one.
 */
export const SERVER_MIN_LEAD_MINUTES = -5;

export const HORIZON_DAYS = 180;

/**
 * How far ahead an immediate booking is actually scheduled.
 *
 * Discord refuses any start at or before now, and the request itself takes
 * time — the deadline budget allows up to twenty seconds before this call is
 * even made. A minute is comfortably clear of both and reads as "in a minute"
 * rather than as a different time from the one she picked.
 */
export const EVENT_START_FLOOR_SECONDS = 60;

/**
 * The instant to give Discord, which is her pick unless that is too close to
 * now to be accepted.
 *
 * @param {string} instant her resolved pick
 * @param {string} [now]
 * @returns {{instant: string, shiftedMs: number}} shiftedMs is 0 when her pick stands
 */
export function startNoEarlierThan(instant, now = new Date().toISOString()) {
  const picked = Temporal.Instant.from(instant).epochMilliseconds;
  const floor = Temporal.Instant.from(now).epochMilliseconds + EVENT_START_FLOOR_SECONDS * 1000;
  if (picked >= floor) return { instant, shiftedMs: 0 };
  return {
    instant: Temporal.Instant.fromEpochMilliseconds(floor).toString(),
    shiftedMs: floor - picked,
  };
}

/**
 * The last selectable calendar day in her zone, inclusive.
 * @param {string} zone
 * @param {string} [nowInstant] ISO instant; defaults to now
 * @returns {string} ISO date, e.g. "2027-03-11"
 */
export function lastSelectableDay(zone, nowInstant = new Date().toISOString()) {
  const today = Temporal.Instant.from(nowInstant).toZonedDateTimeISO(zone).toPlainDate();
  return today.add({ days: HORIZON_DAYS }).toString();
}

/**
 * The first selectable calendar day in her zone — today, in her zone, which is
 * not necessarily today in the browser's.
 * @param {string} zone
 * @param {string} [nowInstant]
 * @returns {string} ISO date
 */
export function firstSelectableDay(zone, nowInstant = new Date().toISOString()) {
  return Temporal.Instant.from(nowInstant).toZonedDateTimeISO(zone).toPlainDate().toString();
}

/**
 * @typedef {{ok: true}} WindowOk
 * @typedef {{ok: false, edge: 'too-soon', minLeadMinutes: number}} WindowTooSoon
 * @typedef {{ok: false, edge: 'too-far', lastDay: string}} WindowTooFar
 */

/**
 * Is this instant inside the booking window?
 *
 * @param {object} args
 * @param {string} args.instant         the resolved UTC instant
 * @param {string} args.zone            her IANA zone, for the calendar-day edge
 * @param {number} args.minLeadMinutes  SERVER_MIN_LEAD_MINUTES or CLIENT_MIN_LEAD_MINUTES
 * @param {string} [args.now]           ISO instant; defaults to now
 * @returns {WindowOk | WindowTooSoon | WindowTooFar}
 */
export function checkBookingWindow({ instant, zone, minLeadMinutes, now = new Date().toISOString() }) {
  const at = Temporal.Instant.from(instant);
  const evaluatedAt = Temporal.Instant.from(now);

  const minutesAhead = at.epochMilliseconds - evaluatedAt.epochMilliseconds;
  if (minutesAhead < minLeadMinutes * 60_000) {
    return { ok: false, edge: 'too-soon', minLeadMinutes };
  }

  // The upper edge is the last moment of the 180th calendar day in her zone.
  const lastDay = lastSelectableDay(zone, now);
  const endOfLastDay = Temporal.PlainDate.from(lastDay)
    .toZonedDateTime({ timeZone: zone, plainTime: Temporal.PlainTime.from('23:59:59.999') })
    .toInstant();

  if (Temporal.Instant.compare(at, endOfLastDay) > 0) {
    return { ok: false, edge: 'too-far', lastDay };
  }

  return { ok: true };
}
